import type {
  ReimbursementIntakeMetadata,
  ReimbursementRequesterLookupResult,
} from './reimbursement.js';

/** Party shape used when resolving reimbursement requester display names. */
export interface ReimbursementRequesterPartyRef {
  legalName?: string | null;
  tradeName?: string | null;
  email?: string | null;
}

export interface ReimbursementRequesterDisplayNameInput {
  requesterEmployeeId?: string | null;
  requesterVendorId?: string | null;
  requesterEmployee?: { party?: ReimbursementRequesterPartyRef | null } | null;
  requesterVendor?: {
    legalName?: string | null;
    tradeName?: string | null;
  } | null;
  intakeMetadata?: ReimbursementIntakeMetadata | null;
}

function trimOrNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : null;
}

function partyDisplayName(party: ReimbursementRequesterPartyRef | null | undefined): string | null {
  if (!party) return null;
  return trimOrNull(party.tradeName) ?? trimOrNull(party.legalName);
}

function vendorDisplayName(
  vendor: ReimbursementRequesterDisplayNameInput['requesterVendor'],
): string | null {
  if (!vendor) return null;
  return trimOrNull(vendor.tradeName) ?? trimOrNull(vendor.legalName);
}

function lookupDisplayName(
  lookup: ReimbursementRequesterLookupResult | null | undefined,
): string | null {
  if (!lookup) return null;
  const displayName = trimOrNull(lookup.displayName);
  if (!displayName) return null;
  // When lookup fell back to free-text intake (NONE), prefer linked employee/vendor over AI text.
  if (lookup.source === 'NONE') return null;
  return displayName;
}

/**
 * Resolves the human-facing reimbursement **requester** (payee) name.
 *
 * Product rule: show who receives the reimbursement, not a dependent/beneficiary
 * name that may appear on a health-plan or family receipt. Linked HCM employee /
 * AP vendor records outrank `intakeMetadata.name` and document extraction text.
 */
export function resolveReimbursementRequesterDisplayName(
  input: ReimbursementRequesterDisplayNameInput,
): string | null {
  const hasLinkedEmployee = Boolean(input.requesterEmployeeId?.trim());
  const hasLinkedVendor = Boolean(input.requesterVendorId?.trim());

  if (hasLinkedEmployee) {
    const fromEmployee = partyDisplayName(input.requesterEmployee?.party);
    if (fromEmployee) return fromEmployee;
  }

  if (hasLinkedVendor) {
    const fromVendor = vendorDisplayName(input.requesterVendor);
    if (fromVendor) return fromVendor;
  }

  const im = input.intakeMetadata;
  const fromLookup = lookupDisplayName(im?.requesterLookup);
  if (fromLookup) return fromLookup;

  if (!hasLinkedEmployee && !hasLinkedVendor) {
    const fromIntakeName = trimOrNull(im?.name);
    if (fromIntakeName) return fromIntakeName;
  }

  return trimOrNull(im?.email);
}
